import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { environment } from 'src/environments/environment';
import { HttpProgramacionMasivaRepository } from 'src/app/infrastructure/adapters/agenda/http-programacion-masiva.repository';
import { HttpOficinaRepository } from 'src/app/infrastructure/adapters/catalogo/http-oficina.repository';
import { HttpTipoAsesorRealRepository } from 'src/app/infrastructure/adapters/catalogo/http-tipo-asesor-real.repository';
import { HttpAgendaRepository } from 'src/app/infrastructure/adapters/agenda/http-agenda.repository';
import { BeneficiarioRepository } from 'src/app/domain/service/beneficiario/beneficiario-repository';
import { TareaRepository } from 'src/app/domain/service/tarea/tarea-repository';
import { TipoCita } from 'src/app/domain/model/agenda/agenda';
import { Oficina } from 'src/app/domain/model/catalogo/oficina';
import { TipoAsesorReal } from 'src/app/domain/model/catalogo/tipo-asesor-real';
import {
  ProgramacionMasiva,
  ProgramacionMasivaDetalle,
  CrearProgramacionMasivaRequest,
  ESTADO_PROGRAMACION_MASIVA
} from 'src/app/domain/model/agenda/programacion-masiva';
import { TIPOS_CITA_IDS } from 'src/app/common/scheduling-constants';
import { redondearCantidad } from 'src/app/common/cantidad.utils';
// Interfaces para selección de diálogos (no usamos los filtros aquí, solo resultados)
import { ClienteSeleccionado } from '../../../common/busquedas/busqueda-cliente-dialog.component';
import { CentroTrabajoSeleccionado } from '../../../common/busquedas/busqueda-centro-trabajo-dialog.component';
import { TareaSeleccionada } from '../../../common/busquedas/busqueda-tarea-dialog.component';

/**
 * Facade para Programación Masiva de Citas
 *
 * Contiene toda la lógica de negocio y estado reactivo.
 * El componente solo delega a este servicio.
 *
 * Migrado desde: ProgramacionMasivaViewImpl.java + Activity
 */
@Injectable()
export class ProgramacionMasivaFacade {
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly programacionMasivaRepo = inject(
    HttpProgramacionMasivaRepository
  );
  private readonly oficinaRepo = inject(HttpOficinaRepository);
  private readonly tipoAsesorRepo = inject(HttpTipoAsesorRealRepository);
  private readonly agendaRepo = inject(HttpAgendaRepository);
  // Repositorios para campos condicionales (tipos 42 y 43)
  private readonly beneficiarioRepo = inject(BeneficiarioRepository);
  private readonly tareaRepo = inject(TareaRepository);

  // === Estado Reactivo (Signals) ===
  readonly loading = signal(false);
  readonly tiposCita = signal<TipoCita[]>([]);
  readonly oficinas = signal<Oficina[]>([]);
  readonly tiposAsesor = signal<TipoAsesorReal[]>([]);
  readonly asesoresSeleccionados = signal<ProgramacionMasivaDetalle[]>([]);
  readonly asesoresDisponibles = signal<ProgramacionMasivaDetalle[]>([]);
  readonly programacionActual = signal<ProgramacionMasiva | null>(null);
  // Legacy: Los resultados se guardan para el diálogo de errores
  // (la tabla principal se limpia después de ejecutar, pero el diálogo los necesita)
  readonly asesoresResultado = signal<ProgramacionMasivaDetalle[]>([]);

  // === Estado del Formulario (incluye campos condicionales) ===
  readonly formState = signal({
    tipoCita: null as number | null,
    fechaCita: null as Date | null,
    horaInicio: null as Date | null,
    duracionTime: null as Date | null,
    oficina: null as number | null,
    observaciones: '',
    tiposAsesorSeleccionados: [] as string[],
    // Campos condicionales (Legacy: tableComplementarios para tipos 42 y 43)
    beneficiarioId: null as number | null,
    beneficiarioContrato: '',
    beneficiarioIdentificacion: '',
    beneficiarioNombre: '',
    centroTrabajoId: null as number | null,
    centroTrabajoNombre: '',
    tareaId: null as number | null,
    tareaNombre: ''
  });

  // === Estado de Diálogos ===
  readonly mostrarDialogAsesores = signal(false);
  readonly mostrarDialogErrores = signal(false);
  // Diálogos para campos condicionales
  readonly mostrarDialogBeneficiario = signal(false);
  readonly mostrarDialogCentroTrabajo = signal(false);
  readonly mostrarDialogTarea = signal(false);

  // === Estado de Ejecución ===
  // Flag que indica que se ejecutó la programación (con o sin errores)
  // Legacy: después de ejecutar, se habilita botón "Nueva" para iniciar otra
  readonly ejecucionCompletada = signal(false);

  // === Computed ===
  readonly programacionCreada = computed(
    () => this.programacionActual() !== null
  );

  /**
   * Legacy: después de ejecutar programarCitas, los campos se bloquean y
   * la tabla de asesores se limpia. Solo se puede hacer "Nueva Programación".
   *
   * Se considera ejecutada si el flag ejecucionCompletada es true.
   */
  readonly programacionEjecutada = computed(() => {
    return this.ejecucionCompletada();
  });

  /**
   * Legacy: Si hay asesores con cita creada (nmconsecutivoCita != null),
   * los campos fecha, hora, duración y observaciones se bloquean.
   * El usuario no puede modificar estos campos si ya hay citas programadas.
   */
  readonly hayAsesoresProgramados = computed(() => {
    return this.asesoresSeleccionados().some(
      (a) => a.nmconsecutivoCita != null
    );
  });

  /**
   * Indica si hay asesores pendientes por programar (sin nmconsecutivoCita).
   * El botón "Programar Cita" solo aparece si hay pendientes.
   */
  readonly hayAsesoresPendientes = computed(() => {
    return this.asesoresSeleccionados().some(
      (a) => a.nmconsecutivoCita == null
    );
  });

  /**
   * Legacy: Los asesores con error se muestran en el diálogo.
   */
  readonly asesoresConError = computed(() =>
    this.asesoresResultado().filter((a) => a.error)
  );

  /**
   * Legacy: tableComplementarios visible cuando tipoCita = 42 o 43
   */
  readonly mostrarCamposComplementarios = computed(() => {
    const tipoCita = this.formState().tipoCita;
    return (
      tipoCita === TIPOS_CITA_IDS.CAMPO_COMPLEMENTARIO ||
      tipoCita === TIPOS_CITA_IDS.INFORME_COMPLEMENTARIO
    );
  });

  readonly puedeCrearProgramacion = computed(() => {
    const form = this.formState();
    const basico = !!(
      form.tipoCita &&
      form.fechaCita &&
      form.horaInicio &&
      this.getDuracionHoras() > 0 &&
      form.oficina &&
      form.tiposAsesorSeleccionados.length > 0
    );

    // Si requiere campos complementarios, validarlos también
    if (this.mostrarCamposComplementarios()) {
      return (
        basico &&
        !!(form.beneficiarioId && form.centroTrabajoId && form.tareaId)
      );
    }
    return basico;
  });

  // === Métodos Privados ===
  private getDuracionHoras(): number {
    const duracion = this.formState().duracionTime;
    if (!duracion) return 0;
    return redondearCantidad(
      duracion.getHours() + duracion.getMinutes() / 60
    );
  }

  private combinarFechaHora(fecha: Date, hora: Date): Date {
    const resultado = new Date(fecha);
    resultado.setHours(hora.getHours(), hora.getMinutes(), 0, 0);
    return resultado;
  }

  /**
   * Formatea fecha en formato ISO local sin conversión a UTC
   * Evita el problema de toISOString() que convierte a UTC
   */
  private formatearFechaLocal(fecha: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${fecha.getFullYear()}-${pad(fecha.getMonth() + 1)}-${pad(fecha.getDate())}T${pad(fecha.getHours())}:${pad(fecha.getMinutes())}:${pad(fecha.getSeconds())}`;
  }

  // === Acciones Públicas ===

  /**
   * Actualiza un campo del formulario
   */
  updateForm(
    campo: keyof ReturnType<typeof this.formState>,
    valor: unknown
  ): void {
    this.formState.update((state) => ({ ...state, [campo]: valor }));
  }

  /**
   * Carga los catálogos necesarios al iniciar
   */
  async cargarCatalogos(): Promise<void> {
    this.loading.set(true);
    try {
      const [tiposCita, oficinas, tiposAsesor] = await Promise.all([
        this.agendaRepo.listarTiposCita(),
        this.oficinaRepo.listarActivas(),
        this.tipoAsesorRepo.listarActivos()
      ]);

      this.tiposCita.set(tiposCita);
      this.oficinas.set(oficinas);
      this.tiposAsesor.set(tiposAsesor);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error al cargar catálogos'
      });
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Valida campos para buscar asesores (solo oficina y tipo asesor)
   */
  validarCamposBusqueda(): string[] {
    const errores: string[] = [];
    const form = this.formState();

    if (!form.oficina) {
      errores.push('Seleccione la oficina');
    }
    if (form.tiposAsesorSeleccionados.length === 0) {
      errores.push('Seleccione al menos un tipo de consultor');
    }

    return errores;
  }

  /**
   * Valida todos los campos obligatorios para programar
   * Legacy: Incluye validación de campos condicionales para tipos 42 y 43
   */
  validarCamposObligatorios(): string[] {
    const errores: string[] = [];
    const form = this.formState();

    if (!form.tipoCita) {
      errores.push('Seleccione el tipo de programación de la lista');
    }
    if (!form.fechaCita) {
      errores.push('Seleccione la fecha de la cita');
    }
    if (!form.horaInicio) {
      errores.push('Seleccione la hora de inicio');
    }
    if (this.getDuracionHoras() <= 0) {
      errores.push('La duración debe ser mayor a 0');
    }
    if (!form.oficina) {
      errores.push('Seleccione la oficina');
    }
    if (form.tiposAsesorSeleccionados.length === 0) {
      errores.push('Seleccione al menos un tipo de consultor');
    }

    // Legacy: Validar campos condicionales para tipos CAMPO_COMPLEMENTARIO (42) e INFORME_COMPLEMENTARIO (43)
    // tbCentroTrabajo.setRequired(true); tbNomTarea.setRequired(true); idBeneficiariosBuscadorView.getTbContrato().setRequired(true);
    if (this.mostrarCamposComplementarios()) {
      if (!form.beneficiarioId) {
        errores.push('Seleccione un beneficiario');
      }
      if (!form.centroTrabajoId) {
        errores.push('Seleccione un centro de trabajo');
      }
      if (!form.tareaId) {
        errores.push('Seleccione una tarea');
      }
    }

    return errores;
  }

  /**
   * Abre el diálogo de búsqueda de asesores
   * Solo valida oficina y tipo de asesor
   */
  async abrirDialogoAsesores(): Promise<void> {
    const errores = this.validarCamposBusqueda();
    if (errores.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error de validación',
        detail: `Por favor ingrese correctamente los siguientes campos:\n${errores.join('\n')}`,
        life: 5000
      });
      return;
    }

    await this.buscarAsesores();
    this.mostrarDialogAsesores.set(true);
  }

  /**
   * Busca asesores disponibles según los filtros
   */
  async buscarAsesores(): Promise<void> {
    const form = this.formState();
    if (!form.oficina || form.tiposAsesorSeleccionados.length === 0) return;

    this.loading.set(true);
    try {
      const asesores = await this.programacionMasivaRepo.buscarAsesores({
        tiposAsesor: form.tiposAsesorSeleccionados,
        oficina: form.oficina
      });

      // Filtrar los que ya están seleccionados
      const seleccionadosIds = new Set(
        this.asesoresSeleccionados().map((a) => a.nmconsecutivoAsesor)
      );
      const disponibles = asesores.filter(
        (a) => !seleccionadosIds.has(a.nmconsecutivoAsesor)
      );

      this.asesoresDisponibles.set(disponibles);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error al buscar consultores'
      });
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Agrega asesores a la lista de seleccionados
   */
  agregarAsesores(asesores: ProgramacionMasivaDetalle[]): void {
    const actuales = this.asesoresSeleccionados();
    this.asesoresSeleccionados.set([...actuales, ...asesores]);

    // Quitar de disponibles
    const agregadosIds = new Set(asesores.map((a) => a.nmconsecutivoAsesor));
    this.asesoresDisponibles.update((list) =>
      list.filter((a) => !agregadosIds.has(a.nmconsecutivoAsesor))
    );

    this.mostrarDialogAsesores.set(false);
  }

  /**
   * Elimina un asesor de la lista de seleccionados
   */
  eliminarAsesor(asesor: ProgramacionMasivaDetalle): void {
    this.asesoresSeleccionados.update((list) =>
      list.filter((a) => a.nmconsecutivoAsesor !== asesor.nmconsecutivoAsesor)
    );
  }

  /**
   * Limpia solo la selección de consultores y estados asociados.
   * No altera otros filtros del formulario.
   */
  limpiarConsultores(): void {
    this.asesoresSeleccionados.set([]);
    this.asesoresDisponibles.set([]);
    this.mostrarDialogAsesores.set(false);
  }

  /**
   * Programa las citas para los asesores pendientes (sin nmconsecutivoCita)
   * Legacy: Incluye campos condicionales para tipos 42 y 43
   *
   * IMPORTANTE: Solo envía asesores que NO tienen cita ya creada.
   * Los que ya tienen nmconsecutivoCita se mantienen en la lista pero no se envían.
   */
  async programarCitas(): Promise<void> {
    const errores = this.validarCamposObligatorios();
    if (errores.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error de validación',
        detail: `Por favor ingrese correctamente los siguientes campos:\n${errores.join('\n')}`,
        life: 5000
      });
      return;
    }

    // Filtrar solo asesores pendientes (sin cita creada)
    const asesoresPendientes = this.asesoresSeleccionados().filter(
      (a) => a.nmconsecutivoCita == null
    );

    if (asesoresPendientes.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'No hay consultores pendientes por programar'
      });
      return;
    }

    this.loading.set(true);
    const form = this.formState();

    try {
      const fechaHora = this.combinarFechaHora(
        form.fechaCita!,
        form.horaInicio!
      );

      // Construir request con campos condicionales
      const request: CrearProgramacionMasivaRequest = {
        tipoCita: form.tipoCita!,
        feprogramacion: this.formatearFechaLocal(fechaHora),
        duracion: this.getDuracionHoras(),
        oficina: form.oficina!,
        observaciones: form.observaciones || undefined
      };

      // Agregar campos condicionales si aplica (tipos 42 y 43)
      if (this.mostrarCamposComplementarios()) {
        if (form.beneficiarioId)
          request.nmconsecutivoBenef = form.beneficiarioId;
        if (form.centroTrabajoId)
          request.nmconsecBenefCt = form.centroTrabajoId;
        if (form.tareaId) request.cdtarea = form.tareaId;
      }

      // 1. Usar programación existente o crear una nueva
      // Legacy: Reutiliza la misma programación masiva si ya existe
      let programacion = this.programacionActual();

      if (!programacion) {
        // Primera vez: crear programación

        programacion = await this.programacionMasivaRepo.crear(request);
        this.programacionActual.set(programacion);
      }

      // 2. Agregar SOLO asesores NUEVOS (sin nmconsecutivo del detalle)
      // Los que ya tienen nmconsecutivo ya están en TPSC_PROG_MASIVA_DET, no re-agregar
      const asesoresNuevos = asesoresPendientes.filter(
        (a) => a.nmconsecutivo == null
      );
      const asesoresIds = asesoresNuevos.map((a) => a.nmconsecutivoAsesor);

      if (asesoresIds.length > 0) {
        await this.programacionMasivaRepo.agregarAsesoresEnLote(
          programacion.nmconsecutivo!,
          {
            asesores: asesoresIds
          }
        );
      }

      // 3. Ejecutar programación (pasar campos condicionales para el SP)
      const camposCondicionales = this.mostrarCamposComplementarios()
        ? {
            nmconsecutivoBenef: form.beneficiarioId ?? undefined,
            nmconsecBenefCt: form.centroTrabajoId ?? undefined,
            cdtarea: form.tareaId ?? undefined
          }
        : undefined;

      const resultado = await this.programacionMasivaRepo.ejecutar(
        programacion.nmconsecutivo!,
        camposCondicionales
      );
      this.programacionActual.set(resultado);

      // 4. Recargar TODOS los asesores de esta programación para ver resultados
      // (incluye los que ya tenían cita de ejecuciones anteriores + los nuevos)
      const todosLosAsesores = await this.programacionMasivaRepo.listarAsesores(
        programacion.nmconsecutivo!
      );
      this.asesoresSeleccionados.set(todosLosAsesores);

      // Legacy: Mostrar en el diálogo TODOS los que se intentaron programar y fallaron
      // asesoresPendientes = todos los que no tenían cita antes de ejecutar (A, B, E, F)
      const idsIntentados = asesoresPendientes.map(
        (a) => a.nmconsecutivoAsesor
      );

      const asesoresConError = todosLosAsesores.filter(
        (a) => idsIntentados.includes(a.nmconsecutivoAsesor!) && a.error
      );
      this.asesoresResultado.set(asesoresConError);

      // Contar los que lograron obtener cita en esta ejecución
      const nuevosConCita = todosLosAsesores.filter(
        (a) =>
          idsIntentados.includes(a.nmconsecutivoAsesor!) &&
          a.nmconsecutivoCita != null
      );

      // Legacy: SOLO abrir diálogo si hay errores (No Programados)
      if (asesoresConError.length > 0) {
        this.mostrarDialogErrores.set(true);
      }

      // Legacy: Mostrar mensaje según resultado (solo de esta ejecución)
      const cantidadErrores = asesoresConError.length;
      const cantidadExitosas = nuevosConCita.length;

      if (cantidadErrores > 0) {
        // Legacy: response.isSuccessFulWithMessage() → Warning
        this.messageService.add({
          severity: 'warn',
          summary: 'Advertencia',
          detail: `${cantidadExitosas} citas creadas, ${cantidadErrores} con error`,
          life: 8000
        });
      } else {
        // Legacy: successfulSaved → Success
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: `${cantidadExitosas} citas creadas exitosamente`
        });
      }

      // Marcar ejecución completada para habilitar botón "Nueva"
      this.ejecucionCompletada.set(true);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error al crear la programación masiva'
      });
    } finally {
      this.loading.set(false);
    }
  }

  // ==================== BÚSQUEDA DE BENEFICIARIO ====================

  /**
   * Abre el diálogo de búsqueda de beneficiario
   * Legacy: idBeneficiariosBuscadorView
   */
  abrirDialogoBeneficiario(): void {
    this.mostrarDialogBeneficiario.set(true);
  }

  cerrarDialogoBeneficiario(): void {
    this.mostrarDialogBeneficiario.set(false);
  }

  /**
   * Maneja la selección de un beneficiario
   * Legacy: idBeneficiariosBuscadorView.getBeneficiario()
   */
  seleccionarBeneficiario(cliente: ClienteSeleccionado): void {
    this.formState.update((state) => ({
      ...state,
      beneficiarioId: cliente.nmconsecutivo,
      beneficiarioContrato: cliente.contrato || '',
      beneficiarioIdentificacion: `${cliente.descTipoIdentificacion || cliente.tipoIdentificacion} ${cliente.numeroIdentificacion}`,
      beneficiarioNombre: cliente.nombreComercial,
      // Limpiar centro de trabajo cuando cambia el beneficiario
      centroTrabajoId: null,
      centroTrabajoNombre: ''
    }));
    this.mostrarDialogBeneficiario.set(false);
  }

  // ==================== BÚSQUEDA DE CENTRO DE TRABAJO ====================

  /**
   * Abre el diálogo de búsqueda de centro de trabajo
   * Legacy: tbCentroTrabajo (requiere beneficiario seleccionado)
   */
  abrirDialogoCentroTrabajo(): void {
    const form = this.formState();
    if (!form.beneficiarioId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'Debe seleccionar un beneficiario primero'
      });
      return;
    }
    this.mostrarDialogCentroTrabajo.set(true);
  }

  cerrarDialogoCentroTrabajo(): void {
    this.mostrarDialogCentroTrabajo.set(false);
  }

  /**
   * Maneja la selección de un centro de trabajo
   * Legacy: centroTrabajoDto.setNmconsecutivo(...)
   */
  seleccionarCentroTrabajo(centroTrabajo: CentroTrabajoSeleccionado): void {
    this.formState.update((state) => ({
      ...state,
      centroTrabajoId: centroTrabajo.nmconsecutivo,
      centroTrabajoNombre:
        centroTrabajo.descripcionCompleta || centroTrabajo.dscentroTrabajo
    }));
    this.mostrarDialogCentroTrabajo.set(false);
  }

  // ==================== BÚSQUEDA DE TAREA ====================

  /**
   * Abre el diálogo de búsqueda de tarea
   * Legacy: busquedaTarea (BusquedaActivity)
   */
  abrirDialogoTarea(): void {
    this.mostrarDialogTarea.set(true);
  }

  cerrarDialogoTarea(): void {
    this.mostrarDialogTarea.set(false);
  }

  /**
   * Maneja la selección de una tarea
   * Legacy: codigoTarea = campos[0]; tbNomTarea.setValue(codigo + " - " + campos[1])
   */
  seleccionarTarea(tarea: TareaSeleccionada): void {
    this.formState.update((state) => ({
      ...state,
      tareaId: tarea.nmconsecutivo,
      tareaNombre: tarea.descripcionCompleta
    }));
    this.mostrarDialogTarea.set(false);
  }

  /**
   * Cierra el diálogo de asesores
   */
  cerrarDialogoAsesores(): void {
    this.mostrarDialogAsesores.set(false);
  }

  /**
   * Cierra el diálogo de errores
   */
  cerrarDialogoErrores(): void {
    this.mostrarDialogErrores.set(false);
  }

  /**
   * Navega de regreso a la pantalla de agenda
   */
  regresar(): void {
    this.router.navigate(['/agenda']);
  }

  /**
   * Limpia el formulario (Legacy: siLimpiar → reset())
   * Solo limpia campos del formulario y asesores, pero mantiene el estado de ejecución.
   * El usuario puede limpiar y empezar de nuevo sin necesidad del botón "Nueva".
   */
  limpiar(): void {
    this.formState.set({
      tipoCita: null,
      fechaCita: null,
      horaInicio: null,
      duracionTime: null,
      oficina: null,
      observaciones: '',
      tiposAsesorSeleccionados: [],
      // Campos condicionales
      beneficiarioId: null,
      beneficiarioContrato: '',
      beneficiarioIdentificacion: '',
      beneficiarioNombre: '',
      centroTrabajoId: null,
      centroTrabajoNombre: '',
      tareaId: null,
      tareaNombre: ''
    });
    this.asesoresSeleccionados.set([]);
    this.asesoresDisponibles.set([]);
    this.mostrarDialogAsesores.set(false);
    // NO limpia programacionActual, asesoresResultado, ni ejecucionCompletada
    // El usuario puede ver "No Programados" después de limpiar
  }

  /**
   * Resetea el estado para una nueva programación
   * Legacy: saNuevaProgramacion → setNmConsecutivoProgMasiva(null) + reset()
   * Limpia TODO incluyendo la programación actual
   */
  reset(): void {
    this.formState.set({
      tipoCita: null,
      fechaCita: null,
      horaInicio: null,
      duracionTime: null,
      oficina: null,
      observaciones: '',
      tiposAsesorSeleccionados: [],
      // Campos condicionales (Legacy: tableComplementarios)
      beneficiarioId: null,
      beneficiarioContrato: '',
      beneficiarioIdentificacion: '',
      beneficiarioNombre: '',
      centroTrabajoId: null,
      centroTrabajoNombre: '',
      tareaId: null,
      tareaNombre: ''
    });
    this.asesoresSeleccionados.set([]);
    this.asesoresDisponibles.set([]);
    this.asesoresResultado.set([]); // Legacy: limpiar resultados de programación anterior
    this.programacionActual.set(null);
    this.ejecucionCompletada.set(false); // Resetear flag de ejecución
    this.mostrarDialogAsesores.set(false);
    this.mostrarDialogErrores.set(false);
    // Diálogos de campos condicionales
    this.mostrarDialogBeneficiario.set(false);
    this.mostrarDialogCentroTrabajo.set(false);
    this.mostrarDialogTarea.set(false);
  }

  /**
   * Limpia los campos condicionales cuando se cambia el tipo de cita
   * Legacy: cuando cambia cbTipoProgramacion, si no es 42/43, limpiar tableComplementarios
   */
  limpiarCamposComplementarios(): void {
    this.formState.update((state) => ({
      ...state,
      beneficiarioId: null,
      beneficiarioContrato: '',
      beneficiarioIdentificacion: '',
      beneficiarioNombre: '',
      centroTrabajoId: null,
      centroTrabajoNombre: '',
      tareaId: null,
      tareaNombre: ''
    }));
  }
}
